import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  InputGroup,
  InputLeftAddon,
  NumberInput,
  NumberInputField,
  Spinner,
} from "@chakra-ui/react";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useContractState } from "../../../useContractState";
import { RemoveDataFieldButton } from "../RemoveDataFieldButton";

interface Props {
  label: string;
  placeholderText: string;
  amount: number | null;
  errorMessage: string | null;
  editAmount: (amount: number | undefined) => Promise<void> | void;
  helperText: string;
  removeDataField?: () => Promise<void> | void;
  isRequiredButNotProvided: boolean;
}

export const GenericMonetaryValue = ({
  label,
  placeholderText,
  amount,
  errorMessage,
  editAmount,
  helperText,
  removeDataField,
  isRequiredButNotProvided,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const currency = useContractState().contract.currency;
  const [providedAmount, setProvidedAmount] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (amount !== undefined && amount !== null && providedAmount === undefined) setProvidedAmount(amount ?? undefined);
  }, [amount, providedAmount]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedAmountEventHandler = useCallback(
    debounce(async (value: number | undefined) => {
      setIsLoading(true);
      await editAmount(value);
      setIsLoading(false);
    }, 300),
    [editAmount]
  );

  useEffect(() => {
    return () => {
      debouncedAmountEventHandler.cancel();
    };
  }, [debouncedAmountEventHandler]);

  return (
    <FormControl isInvalid={errorMessage !== null} isRequired={isRequiredButNotProvided}>
      <FormLabel htmlFor={"value"}>
        {label} {isLoading && <Spinner size="xs" />}
      </FormLabel>
      <Box borderRadius={"10px"}>
        <Flex w="100%">
          <InputGroup>
            <InputLeftAddon>{currency}</InputLeftAddon>
            <NumberInput
              bg="smBackground"
              w="100%"
              value={providedAmount}
              placeholder={placeholderText}
              onChange={(_, valueAsNumber) => {
                if (isNaN(valueAsNumber)) {
                  setProvidedAmount(undefined);
                  debouncedAmountEventHandler(undefined);
                } else {
                  setProvidedAmount(valueAsNumber);
                  debouncedAmountEventHandler(valueAsNumber);
                }
              }}
            >
              <NumberInputField borderLeftRadius="0" />
            </NumberInput>
          </InputGroup>
          {removeDataField && <RemoveDataFieldButton removeDataField={removeDataField} />}
        </Flex>
      </Box>
      <FormHelperText>{helperText}</FormHelperText>
      <FormErrorMessage>{errorMessage && errorMessage}</FormErrorMessage>
    </FormControl>
  );
};
